import React, { useEffect, useMemo, useState } from "react";

/** Every list under /user_profile/orders shows this many rows at a time. */
export const ORDER_TABLE_PAGE_SIZE = 20;

/** 1 … current-1 current current+1 … last, collapsed with ellipses once there are many pages. */
function getPageNumbers(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
  const wanted = new Set([1, totalPages, page, page - 1, page + 1]);
  return [...wanted].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
}

/**
 * Slices an already-loaded row array into pages of `pageSize`.
 * Resets to page 1 whenever the row count changes (filter change, refetch).
 */
export function useOrderTablePagination(rows, pageSize = ORDER_TABLE_PAGE_SIZE) {
  const [page, setPage] = useState(1);
  const list = Array.isArray(rows) ? rows : [];
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [list.length]);

  const safePage = Math.min(Math.max(1, page), totalPages);
  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * pageSize;
    return list.slice(start, start + pageSize);
  }, [list, safePage, pageSize]);

  return { page: safePage, setPage, totalPages, pagedRows, pageSize, totalRows: list.length };
}

/** Pager shown under orders tables; renders nothing while everything fits on one page. */
export default function OrderTablePagination({ page, totalPages, onPageChange, label = "Pagination" }) {
  if (!totalPages || totalPages <= 1) return null;
  const numbers = getPageNumbers(page, totalPages);

  return (
    <div className="so-pagination" role="navigation" aria-label={label}>
      <button
        type="button"
        className="so-pagination__nav"
        disabled={page <= 1}
        aria-label="Previous page"
        onClick={() => onPageChange(Math.max(1, page - 1))}
      >
        ‹
      </button>

      {numbers.map((n, i) => {
        const prev = numbers[i - 1];
        return (
          <React.Fragment key={n}>
            {prev && n - prev > 1 ? (
              <span className="so-pagination__dots" aria-hidden="true">
                …
              </span>
            ) : null}
            <button
              type="button"
              className={`so-pagination__num${page === n ? " is-active" : ""}`}
              aria-current={page === n ? "page" : undefined}
              onClick={() => onPageChange(n)}
            >
              {n}
            </button>
          </React.Fragment>
        );
      })}

      <button
        type="button"
        className="so-pagination__nav"
        disabled={page >= totalPages}
        aria-label="Next page"
        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
      >
        ›
      </button>
    </div>
  );
}
